import { ExternalLink, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DocumentDetailsRelease } from "~/api/types";
import { getOfficeURIScheme } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { DocumentArtifactStatus } from "~/config/types";

type ValidationError = "missing-published-artifact" | "invalid-file-type";

interface OfficeUriResult {
  artifactId: number;
  uri: string | null;
}

interface OpenInWordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  release: DocumentDetailsRelease | null;
}

const launchWord = (uri: string) => {
  window.location.href = uri;
};

export function OpenInWordDialog({
  open,
  onOpenChange,
  release,
}: OpenInWordDialogProps) {
  const { t } = useTranslation();
  const [officeUriResult, setOfficeUriResult] =
    useState<OfficeUriResult | null>(null);

  const artifactId = release?.artifact.id;
  const validationError: ValidationError | null =
    release?.artifact.status !== DocumentArtifactStatus.published ||
    release.artifact.publishedAt === null
      ? "missing-published-artifact"
      : release.artifact.fileType !== "docx"
        ? "invalid-file-type"
        : null;

  useEffect(() => {
    if (!open || validationError || artifactId === undefined) return;

    let cancelled = false;

    void getOfficeURIScheme(artifactId)
      .then(({ uri }) => {
        if (cancelled) return;

        setOfficeUriResult({ artifactId, uri });
        if (uri) launchWord(uri);
      })
      .catch(() => {
        if (!cancelled) {
          setOfficeUriResult({ artifactId, uri: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, validationError, artifactId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOfficeUriResult(null);
    }
    onOpenChange(nextOpen);
  };

  const isLoading =
    validationError === null &&
    (officeUriResult === null || officeUriResult.artifactId !== artifactId);
  const officeUri =
    officeUriResult?.artifactId === artifactId
      ? (officeUriResult?.uri ?? null)
      : null;
  const requestFailed =
    validationError === null && !isLoading && officeUri === null;
  const isError = validationError !== null || requestFailed;
  const description = validationError
    ? validationError === "missing-published-artifact"
      ? t(($) => $.commons.workflow.noPublishedVersion)
      : t(($) => $.commons.workflow.wordFileRequired)
    : requestFailed
      ? t(($) => $.commons.workflow.openInWordFailed)
      : t(($) => $.commons.workflow.openingInWordDescription);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isLoading && <LoaderCircle className="size-4 animate-spin" />}
            {isError
              ? t(($) => $.commons.workflow.openInWordFailed)
              : t(($) => $.commons.workflow.openingInWord)}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t(($) => $.commons.buttons.close)}
            </Button>
          </DialogClose>
          {officeUri && (
            <Button type="button" onClick={() => launchWord(officeUri)}>
              <ExternalLink data-icon="inline-start" />
              {t(($) => $.commons.workflow.openInOffice, {
                officeType: "Word",
              })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
